import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import api from '../utils/api';

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    loadNotifications();
    
    // Poll for new notifications every 10 seconds
    const interval = setInterval(() => {
      loadNotifications(true);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async (silent = false) => {
    try {
      const [notificationsRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count'),
      ]);
      
      const newNotifications = notificationsRes.data;
      const newUnreadCount = countRes.data.count;
      
      // Show toast for new notifications (only if not silent initial load)
      if (!silent && newUnreadCount > unreadCount) {
        const latestUnread = newNotifications.find(n => !n.read);
        if (latestUnread) {
          toast(latestUnread.message, {
            icon: getNotificationIcon(latestUnread.type),
            action: {
              label: 'View',
              onClick: () => navigate(`/auctions/${latestUnread.auction_id}`),
            },
          });
        }
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      // Silently fail for background polling
      if (!silent) {
        console.error('Failed to load notifications:', error);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      loadNotifications(true);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      loadNotifications(true);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notification_id);
    setOpen(false);
    navigate(`/auctions/${notification.auction_id}`);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'outbid':
        return '🔔';
      case 'auction_ending':
        return '⏰';
      case 'auction_won':
        return '🎉';
      default:
        return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'outbid':
        return 'text-destructive';
      case 'auction_ending':
        return 'text-orange-500';
      case 'auction_won':
        return 'text-success';
      default:
        return 'text-foreground';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 w-9 rounded-sm p-0 relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 h-5 w-5 bg-destructive text-white text-xs font-bold font-mono flex items-center justify-center rounded-sm"
              data-testid="notification-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-dropdown">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h3 className="font-semibold font-heading text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-7 px-2 text-xs rounded-sm"
              data-testid="mark-all-read-button"
            >
              <Check className="h-3 w-3 mr-1" strokeWidth={1.5} />
              Mark all read
            </Button>
          )}
        </div>

        <ScrollArea className="h-96">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="py-2">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 cursor-pointer hover:bg-accent transition-colors border-l-2 ${
                    notification.read
                      ? 'border-transparent bg-background'
                      : 'border-primary bg-accent/50'
                  }`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${getNotificationColor(notification.type)} ${
                        !notification.read ? 'font-semibold' : ''
                      }`}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {notification.auction_title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="h-2 w-2 bg-primary rounded-full flex-shrink-0 mt-1"></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button
                variant="ghost"
                className="w-full h-8 text-xs rounded-sm"
                onClick={() => {
                  setOpen(false);
                  navigate('/dashboard');
                }}
              >
                View Dashboard
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
